import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge, Tooltip } from "antd";
import { DownOutlined, LogoutOutlined } from "@ant-design/icons";
import { useCompanyStore } from "../../store/slices/company";
import { useUserStore } from "../../store/slices/user";
import { useTerm } from "../../lib/terminology";
import InitialsAvatar from "../../components/ui/InitialsAvatar";

import { AppRoute } from "./routes";
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from "./layoutConstants";

interface SidebarMenuProps {
  collapsed?: boolean;
  routes?: AppRoute[];
}

const SidebarMenu = ({
  collapsed = false,
  routes = [],
}: SidebarMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useCompanyStore();
  const { user, logout } = useUserStore();
  const term = useTerm();

  // terminology-aware label overrides for key routes
  const termLabels: Record<string, string> = {
    "/jobs":          term("job", true),
    "/quotes":        term("quote", true),
    "/clients":       term("client", true),
    "/field-workers": term("worker", true),
    "/invoices":      term("invoice", true),
    "/contracts":     term("contract", true),
    "/schedule":      term("schedule", true),
    "/reports":       term("report", true),
  };


const isActive = (url?: string) => {
  if (!url) return false;

  // Home/Dashboard route
  if (url === "/") {
    return location.pathname === "/";
  }

  // Exact match
  if (location.pathname === url) {
    return true;
  }

  // Nested routes
  return location.pathname.startsWith(url + "/");
};
  const groupedRoutes = routes.reduce((acc: any, item: any) => {
    const section = item.type || "MAIN";
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  const renderMenuItem = (item: any) => {
    const active = isActive(item.url);
    const label = (item.url && termLabels[item.url]) ? termLabels[item.url] : item.label;

    const content = (
      <>
        {/* ICON */}
        <span className="text-blue-400 text-[16px] shrink-0">
          {item.icon && <item.icon />}
        </span>

        {!collapsed && (
          <>
            {/* LABEL */}
            <span className="text-sm truncate">
              {label}
            </span>

            {/* BADGE */}
{item.count ? (
  <Badge
    count={item.count}
    size="small"
    style={{
      background: item.badgeColor || "#3B82F6",
      boxShadow: "none",
      fontWeight: 600,
      fontSize: 11,
      minWidth: 20,
      height: 20,
      lineHeight: "20px",
      borderRadius: "9999px",
      padding: 0,
    }}
    className="ml-auto"
  />
) : null}
          </>
        )}
      </>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.key} title={label} placement="right">
          <div
            onClick={() => navigate(item.url)}
            className={`h-11 w-11 mx-auto rounded-lg flex items-center justify-center cursor-pointer ${
              active ? "bg-[#274690] text-white" : "text-blue-200"
            }`}
          >
            {item.icon && <item.icon className="w-4 h-4" />}
          </div>
        </Tooltip>
      );
    }

    return (
      <div
  key={item.key}
  onClick={() => navigate(item.url)}
  className={`nav-item flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-all duration-150 ${
    active ? "active text-white" : "text-blue-200"
  }`}
>
  {content}
</div>
    );
  };

  return (
    <aside
      className="bg-blue-950 flex flex-col justify-between flex-shrink-0 h-screen z-50"
      style={{ width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED }}
    >
      {/* LOGO */}
      {!collapsed && (

          <div className="sidebar-logo flex items-center gap-3 px-5 py-4 mb-2">
      <div className="w-10 h-8  rounded-lg flex items-center justify-center">
              <img src={company?.logo_url || "/sitepro_logo.png"} alt="SitePro Logo" className="w-full h-full object-contain" />

      </div>
      <div>
        <div className="text-white font-700 text-sm font-semibold">ARRUK</div>
        <div className="text-blue-300 text-xs">UK Field Service</div>
      </div>
    </div>)}
{collapsed && (

  <Tooltip
    key="FIELD_SERVICES"
    title="ARRUK"
    placement="right"
  >
    <div className="sidebar-logo flex justify-center gap-3 px-5 py-4 mb-2">
      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
        <svg
          className="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      </div>
    </div>
  </Tooltip>
  
)}

      {/* COMPANY SWITCHER */}
{!collapsed && (
  <div className="mx-3 mb-4 bg-blue-900/60 hover:bg-blue-900 rounded-lg px-3 py-2 flex items-center gap-2 cursor-pointer transition-colors">
    
    {/* AVATAR */}
    <InitialsAvatar
      name={company?.name || "Company"}
      src={company?.logo_url}
      size={28}
      className="!rounded-md"
    />

    <div className="flex-1 min-w-0">
      {/* NAME */}
      <div className="text-white text-xs font-semibold truncate">
        {company?.name || "—"}
      </div>

      {/* PLAN (from subscription) */}
      <div className="text-blue-400 text-xs truncate">
        {company?.subscription?.plan_name || "No plan"}
      </div>

      {/* INDUSTRY TAGS */}
      {company?.industries && company.industries.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {company.industries.slice(0, 2).map((ind) => (
            <span
              key={ind.id}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-800 text-blue-200 leading-none"
              title={ind.is_primary ? "Primary industry" : undefined}
            >
              {ind.name}{ind.is_primary ? " ★" : ""}
            </span>
          ))}
          {company.industries.length > 2 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-800 text-blue-300 leading-none">
              +{company.industries.length - 2}
            </span>
          )}
        </div>
      )}
    </div>
  </div>
)}

   {collapsed && (
  <Tooltip
    key="Plan"
    title={company?.name || "Company"}
    placement="right"
  >
    <div className=" flex justify-center gap-3 px-5 py-2">
          <InitialsAvatar name={company?.name || "Company"} src={company?.logo_url} size={32} className="!rounded-md" />
    </div>
  </Tooltip>
)}

      {/* MENU */}
      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto custom-scrollbar">
        {Object.entries(groupedRoutes).map(([section, items]: any) => (
          <div key={section}>
            {!collapsed && (
              <div className="text-blue-500 text-xs font-semibold px-3 py-1 mt-1 uppercase tracking-wider">
                {section}
              </div>
            )}

            <div>{items.map((item: any) => renderMenuItem(item))}</div>
          </div>
        ))}
      </nav>

      {/* USER */}
      <div className="p-3 border-t border-blue-900">
        <div
  className={`flex items-center gap-3 ${
    collapsed ? "justify-center" : ""
  }`}
>
          <InitialsAvatar name={user?.name || "User"} size={32} />

          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-semibold truncate">
                  {user?.name}
                </div>
                <div className="text-blue-400 text-xs">
                  {user?.role_name}
                </div>
              </div>

              <LogoutOutlined
  className="text-blue-500 text-sm cursor-pointer"
  onClick={logout}
/>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SidebarMenu;